import type { Applicant } from "@flow";
import {
  NumberField,
  PrimaryProgressButton,
  RadioButtons,
  Select,
  Text,
  TextArea
} from "@stacc/flow-ui-components";
import type { ChangeEvent, CSSProperties, JSX } from "react";
import { formatCurrency } from "../../utils/helperFunctions";
import * as Styles from "./styles";
import { useTaskPermissions } from "./TaskPermissionsContext";

type OptionValue = string | number | boolean;

interface Option {
  label: string;
  value: OptionValue;
}

interface Message {
  timestamp: string | number;
  mail?: { body?: string };
  sms?: { message?: string };
  call?: { to?: string };
}

export function UsertaskApplicantInfo(
  applicant: Applicant,
  mainApplicant = true
) {
  const applicantFirstName = applicant?.firstName ?? "-";
  const applicantLastName = applicant?.lastName ?? "-";
  const applicantNIF = applicant?.nationalId ?? "-";
  const applicantEmail = applicant?.emailAddress ?? "-";
  const applicantPhone = applicant?.mobileNumber ?? "-";

  return (
    <Styles.DecisionSectionDivWrapper>
      <Styles.DecisionSectionPersonWrapper>
        <Styles.DecisionSectionPersonApplicantWrapper>
          <Styles.DecisionSectionPersonApplicantBold>
            {mainApplicant ? "MA" : "CA"}
          </Styles.DecisionSectionPersonApplicantBold>
        </Styles.DecisionSectionPersonApplicantWrapper>
        <Styles.DecisionSectionPersonInfoWrapper>
          <Styles.DecisionSectionPersonInfoDivWrapper>
            <Styles.DecisionSectionPersonApplicantBold>
              {applicantFirstName} {applicantLastName}
            </Styles.DecisionSectionPersonApplicantBold>
            {applicantNIF}
          </Styles.DecisionSectionPersonInfoDivWrapper>
          <Styles.DecisionSectionPersonInfoDivWrapper
            style={{ alignItems: "flex-end" }}
          >
            <Styles.DecisionSectionPersonInfoAnchor
              href={`tel:${applicantPhone}`}
            >
              {applicantPhone}
            </Styles.DecisionSectionPersonInfoAnchor>
            <Styles.DecisionSectionPersonInfoAnchor
              href={`mailto:${applicantEmail}`}
            >
              {applicantEmail}
            </Styles.DecisionSectionPersonInfoAnchor>
          </Styles.DecisionSectionPersonInfoDivWrapper>
        </Styles.DecisionSectionPersonInfoWrapper>
      </Styles.DecisionSectionPersonWrapper>
    </Styles.DecisionSectionDivWrapper>
  );
}

export function UsertaskDecision(
  decision: OptionValue | null,
  setDecision: (e: ChangeEvent<HTMLInputElement>) => void,
  options: Option[] | null = null
) {
  return (
    <Styles.DecisionSectionDivWrapper>
      <b>Decision</b>
      <RadioButtons
        name="myRadio"
        checkedValue={decision ?? undefined}
        onChange={setDecision}
        options={
          options ?? [
            {
              label: "Continue Loan Application",
              value: true
            },
            {
              label: "Reject Loan Application",
              value: false
            }
          ]
        }
      />
    </Styles.DecisionSectionDivWrapper>
  );
}

export function UsertaskInternalComment(
  comment: string,
  setComment: (e: ChangeEvent<HTMLTextAreaElement>) => void,
  title = ""
) {
  return (
    <Styles.DecisionSectionDivWrapper>
      <b>{title ?? ""}</b>
      <TextArea
        style={{ width: "100%", resize: "none", height: "100px" }}
        name="comment"
        onChange={setComment}
        defaultValue={comment}
      />
    </Styles.DecisionSectionDivWrapper>
  );
}

export function UsertaskSelect(
  label: string,
  options: Option[],
  value: OptionValue | undefined,
  setValue: (value: string) => void,
  style: CSSProperties = {}
) {
  return (
    <Styles.DecisionSectionDivWrapper
      style={{ ...style, marginBottom: "10px" }}
    >
      <Select
        label={label}
        name={label}
        options={options}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          setValue(e.target.value)
        }
        value={value}
      />
    </Styles.DecisionSectionDivWrapper>
  );
}

export function UsertaskTextField(
  text: string,
  setText: (e: ChangeEvent<HTMLInputElement>) => void,
  title = "Title",
  placeholder = "Text"
) {
  const id = `usertask-text-field-${title}`;
  return (
    <Styles.DecisionSectionDivWrapper style={{ margin: "10px 25px" }}>
      <label htmlFor={id} style={{ fontSize: "12px" }}>
        {title}
      </label>
      <Text id={id} name="textField" onChange={setText} defaultValue={text} />
    </Styles.DecisionSectionDivWrapper>
  );
}

export const UsertaskNumberInput = (
  value: number | undefined,
  setValue: ((e: ChangeEvent<HTMLInputElement>) => void) | null,
  title = "Loan Amount",
  min = 0,
  max: number = Number.POSITIVE_INFINITY,
  disabled = false
) => {
  const id = `usertask-number-input-${title}`;
  // NumberField calls onChange(event, value), but its declaration in
  // types/flow-ui-components.d.ts says onChange(value)
  const handleChange = (setValue ?? (() => undefined)) as unknown as (
    value: number
  ) => void;
  return (
    <Styles.DecisionSectionDivWrapper style={{ margin: "10px 25px" }}>
      <label htmlFor={id} style={{ fontSize: "12px" }}>
        {title}
      </label>
      <NumberField
        id={id}
        name="number"
        defaultValue={value}
        onChange={handleChange}
        min={min}
        step={1000}
        max={max}
        disabled={disabled}
      />
    </Styles.DecisionSectionDivWrapper>
  );
};

export function UsertaskTextArea(
  body: string,
  setBody: (value: string) => void,
  placeholder = "Text"
) {
  return (
    <Styles.DecisionSectionDivWrapper style={{ marginTop: "10px" }}>
      <TextArea
        style={{ width: "100%", resize: "none" }}
        name="text"
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
          setBody(e.target.value)
        }
        defaultValue={body}
        help={placeholder}
      />
    </Styles.DecisionSectionDivWrapper>
  );
}

interface UsertaskButtonGroupProps {
  decision: unknown;
  complete: () => void;
  save: (() => void) | null;
}

function UsertaskButtonGroup({
  decision,
  complete,
  save
}: UsertaskButtonGroupProps): JSX.Element {
  const { canAct, canSave } = useTaskPermissions();
  return (
    <Styles.DecisionSectionDivWrapper
      style={{ display: "flex", justifyContent: "space-between" }}
    >
      <PrimaryProgressButton
        style={{ width: "140px" }}
        disabled={decision === null || !canAct}
        onClick={complete}
      >
        Complete
      </PrimaryProgressButton>

      {save && (
        <PrimaryProgressButton
          style={{ width: "140px", backgroundColor: "#B8B8B8" }}
          disabled={!canSave}
          onClick={save}
        >
          Save
        </PrimaryProgressButton>
      )}
    </Styles.DecisionSectionDivWrapper>
  );
}

export function UsertaskButtons(
  decision: unknown,
  complete: () => void,
  save: (() => void) | null = null
) {
  return (
    <UsertaskButtonGroup decision={decision} complete={complete} save={save} />
  );
}

export function renderMessage(message: Message, index: number) {
  const date = new Date(message.timestamp);
  const day = date.toLocaleDateString("en-EN", { weekday: "long" });
  const dayNumber = date.toLocaleDateString("en-EN", { day: "2-digit" });
  const month = date.toLocaleDateString("en-EN", { month: "2-digit" });
  const year = date.toLocaleDateString("en-EN", { year: "numeric" });
  const time = date.toLocaleTimeString("en-EN", {
    hour: "2-digit",
    minute: "2-digit"
  });

  const isMail = message?.mail?.body !== undefined;
  const isCall = message?.call !== undefined;

  if (isCall) {
    return (
      <Styles.CallWrapper key={`Call-${index}`}>
        Called to {message?.call?.to} on {day} {dayNumber}/{month}/{year} {time}
      </Styles.CallWrapper>
    );
  }

  return (
    <Styles.MessageWrapper key={`Message-${index}`}>
      <Styles.MessageContainer>
        <Styles.MessageHeader>
          Sent {day} {dayNumber}/{month}/{year}{" "}
          <Styles.MessageHeaderBold>{time}</Styles.MessageHeaderBold> via{" "}
          {isMail ? "mail" : "SMS"}
        </Styles.MessageHeader>
        <Styles.MessageBody>
          {isMail ? message?.mail?.body : message?.sms?.message}
        </Styles.MessageBody>
      </Styles.MessageContainer>
      <Styles.MessageIcon>CW</Styles.MessageIcon>
    </Styles.MessageWrapper>
  );
}

type SliderProps = {
  min: number;
  max: number;
  value: number;
  onChange: (value: string) => void;
  disabled?: boolean;
};

export function Slider({
  min,
  max,
  value,
  onChange,
  disabled = false
}: SliderProps) {
  return (
    <Styles.SliderContainer>
      <Styles.SliderLabel>Min {formatCurrency(min)}</Styles.SliderLabel>
      <Styles.SliderInput
        disabled={disabled}
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <Styles.SliderLabel>Max {formatCurrency(max)}</Styles.SliderLabel>
    </Styles.SliderContainer>
  );
}
